import { useEffect, useState } from 'react';
import {
  faArrowRight,
  faCheck,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Group, Paper, Stack, Text } from '../../../core';
import { surfaceHeaderProps } from '../SurfaceHeader';

export interface ProposedChange {
  readonly label: string;
  /** Omitted when the view has no current value for the field. */
  readonly from?: string;
  readonly to: string;
}

/**
 * Console-computed pricing for the proposal. Never supplied by the agent —
 * the whole point is that this figure comes from the same endpoint the apply
 * will hit, so it cannot disagree with the result.
 */
export interface ProposalPreview {
  readonly status: 'ok' | 'invalid' | 'failed';
  readonly beforeDisplay?: string;
  readonly afterDisplay?: string;
  readonly deltaDisplay?: string;
  readonly detail?: string;
  /** Stale once the view moves on; the figure is greyed rather than removed. */
  readonly stale?: boolean;
}

/**
 * Written to `/results/<id>` by the host once Apply or Dismiss resolves.
 * `applied` is not the only success: the inputs can change while the re-rate
 * fails, and saying so beats quoting a premium that no longer describes the
 * quote. `dismissed` is a terminal state too — it is what retires the buttons
 * on a proposal the underwriter turned down.
 */
export interface ProposalResult {
  readonly status:
    | 'applied'
    | 'applied-underived'
    | 'applied-pending'
    | 'dismissed'
    | 'rejected';
  readonly message?: string;
}

export interface ViewChangeProposalProps {
  readonly title: string;
  readonly rationale?: string;
  readonly changes?: readonly ProposedChange[];
  readonly preview?: ProposalPreview;
  readonly result?: ProposalResult;
  readonly pending?: boolean;
  readonly onApply?: () => void;
  readonly onDismiss?: () => void;
  /**
   * Fired ONCE on mount, by this component rather than by the underwriter.
   *
   * The console owns the price on this card — that is the whole point of the
   * preview capability — but it cannot ask for it unbidden: the change-set
   * lives in the action binding the agent supplies, and the binder hands us an
   * opaque `() => void`. So the agent binds this the same way it binds
   * `onApply`, and the console decides when to fire it.
   *
   * Unbound, the card degrades to what shipped in phase 2: correct, decidable,
   * and priceless in the literal sense.
   */
  readonly onPreview?: () => void;
}

const RESULT_TONE: Record<
  ProposalResult['status'],
  { readonly icon: typeof faCheck; readonly color: string }
> = {
  applied: { icon: faCheck, color: 'var(--mantine-color-green-7)' },
  'applied-underived': {
    icon: faTriangleExclamation,
    color: 'var(--mantine-color-yellow-8)',
  },
  'applied-pending': { icon: faCheck, color: 'var(--mantine-color-gray-7)' },
  dismissed: { icon: faXmark, color: 'var(--mantine-color-gray-7)' },
  rejected: {
    icon: faTriangleExclamation,
    color: 'var(--mantine-color-red-7)',
  },
};

function PreviewRow({ preview }: { readonly preview: ProposalPreview }) {
  if (preview.status !== 'ok') {
    return (
      <Text size="sm" c="dimmed">
        {preview.detail ?? 'This combination could not be priced.'}
      </Text>
    );
  }

  return (
    <Group gap="xs" align="baseline">
      <Text size="sm" c="dimmed">
        Premium
      </Text>
      <Text size="sm" c={preview.stale ? 'dimmed' : undefined}>
        {preview.beforeDisplay} → <strong>{preview.afterDisplay}</strong>
      </Text>
      {preview.deltaDisplay ? (
        <Text size="xs" c="dimmed">
          ({preview.deltaDisplay})
        </Text>
      ) : null}
      {preview.stale ? (
        <Text size="xs" c="dimmed">
          — preview is out of date
        </Text>
      ) : null}
    </Group>
  );
}

/** Three states, one button. Spelled out because stacked ternaries are banned. */
/**
 * How long to wait for one pricing attempt. Comfortably longer than a real
 * rater call (~1.3s observed) so a slow price is never mistaken for a stall.
 */
const ATTEMPT_MS = 5_000;

/**
 * Ask at most twice.
 *
 * A dispatch can be lost outright: the surface is re-created while it is in
 * flight, and both its result and the mutex clear land on a data model this
 * rendered card no longer observes (console#13173). The model is fine by then
 * — asking again is all that is needed, and the second ask cannot be dropped
 * by the in-flight mutex because the first has long since settled.
 *
 * Two, not unbounded: if a second attempt also vanishes, something is wrong
 * that retrying will not mend, and a card quietly polling the rater forever is
 * worse than one that says so.
 */
const MAX_ATTEMPTS = 2;

function applyLabel(pending: boolean, pricing: boolean): string {
  if (pending) {
    return 'Applying…';
  }
  if (pricing) {
    return 'Pricing…';
  }
  return 'Apply';
}

/**
 * A proposal the underwriter can apply to the quote they are looking at.
 *
 * Nothing here changes the view on its own — that is the propose-then-apply
 * contract. The agent suggests, the underwriter decides, and the figure shown
 * is the console's own, not the agent's.
 */
export function ViewChangeProposal({
  title,
  rationale,
  changes,
  preview,
  result,
  pending,
  onApply,
  onDismiss,
  onPreview,
}: ViewChangeProposalProps) {
  const settled = Boolean(result);
  const tone = result ? RESULT_TONE[result.status] : undefined;

  // State, not a ref: refs are for DOM nodes here, and this is a latch that
  // must survive the re-render its own dispatch causes.
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // `preview`/`result` already present means this card is being replayed
    // onto a rebuilt processor, not mounted fresh — asking for a price we
    // already have would spend a rater call to learn nothing.
    if (!onPreview || attempts > 0 || preview || result) {
      return;
    }
    setAttempts(1);
    onPreview();
  }, [onPreview, attempts, preview, result]);

  /**
   * A pricing attempt that never lands: ask once more, then say so.
   *
   * The auto-preview races the surface's own re-creation. `A2UISurfaceList` can
   * rebuild its processor and re-create the surface while a dispatch is in
   * flight, and both the result and the `/_inFlight` clear are then applied to
   * a data model this rendered card no longer observes (console#13173).
   * Verified live: the model read `_inFlight: false` while this card still
   * showed "Applying…" with dead buttons and no premium, and nothing threw.
   *
   * The window is the dispatch duration, which is why it looked random — a
   * refusal settles in ~4ms and nearly always wins, a real rater call takes
   * ~1.3s and often loses. Now that payloads are valid, every preview does real
   * work, so the losing case is the normal one.
   *
   * A retry is enough because by the time it fires the model has settled: the
   * price is simply never asked for again, since `attempts` latched on the
   * first try and the mutex dropped the card's own re-fires while the original
   * was still out. The mutex cannot drop this one.
   *
   * Buttons stay disabled while a price is genuinely expected — that is not
   * cosmetic, since a click during a live dispatch is dropped with only a debug
   * log. Once we stop expecting one, they go live rather than leaving a card
   * that can only be thrown away.
   */
  /**
   * The deadline for the current attempt. Deliberately NOT keyed on
   * `onPreview`.
   *
   * A2UI's binder returns a fresh closure for every ACTION prop on each render,
   * so an effect that lists `onPreview` re-runs constantly — and a timer inside
   * it is cleared and restarted every time, which can postpone the retry
   * indefinitely and leave the card disabled at "Pricing…" forever. That is the
   * very state this recovery exists to end, so the countdown watches only state
   * that actually changes when something happens.
   */
  const [expired, setExpired] = useState(0);
  useEffect(() => {
    if (attempts === 0 || preview || result || expired >= attempts) {
      return undefined;
    }
    const timer = setTimeout(() => setExpired(attempts), ATTEMPT_MS);
    return () => clearTimeout(timer);
  }, [attempts, expired, preview, result]);

  // Acting on an expired deadline may reference `onPreview`; it holds no timer,
  // so churn only re-runs a guarded no-op. `expired !== attempts` means this
  // deadline has already been answered.
  useEffect(() => {
    if (!onPreview || preview || result) {
      return;
    }
    if (attempts === 0 || expired !== attempts || attempts >= MAX_ATTEMPTS) {
      return;
    }
    setAttempts(attempts + 1);
    onPreview();
  }, [onPreview, attempts, expired, preview, result]);

  const stalled =
    attempts >= MAX_ATTEMPTS && expired === attempts && !preview && !result;

  /**
   * Distrust only the `pending` that stalled — not every `pending` after it.
   *
   * Suppressing `pending` outright for as long as `stalled` held meant a later
   * Apply never disabled its own button or said "Applying…", so a second click
   * was silently dropped by the in-flight mutex: the exact failure the timeout
   * was added to prevent, moved one step downstream.
   *
   * A `pending` that is stuck true has, by definition, never gone false. So
   * distrust begins when the stall finds it true and ends the moment it reads
   * false — at which point the binding is demonstrably alive and its next
   * `true` means a real dispatch worth showing.
   */
  const [clearedSinceStall, setClearedSinceStall] = useState(false);
  useEffect(() => {
    if (!stalled) {
      setClearedSinceStall(false);
      return;
    }
    if (!pending) {
      setClearedSinceStall(true);
    }
  }, [stalled, pending]);

  // Distrusted only while it has never gone false since the stall began. Keying
  // this on `stalled` alone re-condemned every later `pending` for as long as
  // the stall held, which is the same silent-drop bug one step downstream.
  const reallyPending = Boolean(pending) && !(stalled && !clearedSinceStall);
  const pricing = attempts > 0 && !preview && !result && !stalled;
  const busy = reallyPending || pricing;

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <div {...surfaceHeaderProps('md', 'none')}>
          <Text fw={600}>{title}</Text>
        </div>
        {rationale ? (
          <Text size="sm" c="dimmed">
            {rationale}
          </Text>
        ) : null}

        <Stack gap={4}>
          {(changes ?? []).map((change) => (
            <Group
              key={`${change.label}:${change.to}`}
              gap="xs"
              align="baseline"
            >
              <Text size="sm" c="dimmed" style={{ minWidth: '9rem' }}>
                {change.label}
              </Text>
              {change.from ? (
                <>
                  <Text size="sm">{change.from}</Text>
                  <FontAwesomeIcon icon={faArrowRight} size="xs" />
                </>
              ) : null}
              <Text size="sm" fw={600}>
                {change.to}
              </Text>
            </Group>
          ))}
        </Stack>

        {/* A settled result supersedes the preview: the two describe the same
            proposal at different moments, and rendering both showed a refusal
            twice in the same card — the estimate that never resolved, directly
            above the outcome that explains why. */}
        {preview && !result ? <PreviewRow preview={preview} /> : null}

        {stalled && !preview && !result ? (
          <Text size="sm" c="dimmed">
            The price didn&apos;t come back. You can still apply this.
          </Text>
        ) : null}

        {result && tone ? (
          <Group gap="xs" align="baseline">
            <FontAwesomeIcon icon={tone.icon} color={tone.color} size="sm" />
            <Text size="sm">{result.message}</Text>
          </Group>
        ) : (
          <Box>
            <Group gap="sm">
              <Button size="compact-sm" onClick={onApply} disabled={busy}>
                {applyLabel(reallyPending, pricing)}
              </Button>
              <Button
                size="compact-sm"
                variant="subtle"
                onClick={onDismiss}
                disabled={busy}
              >
                Dismiss
              </Button>
            </Group>
          </Box>
        )}

        {settled ? null : (
          <Text size="xs" c="dimmed">
            Nothing changes until you apply this.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
